import { useLazyQuery, useQuery } from '@apollo/client/react';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import {
  Autocomplete,
  Button,
  createFilterOptions,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  LinearProgress,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { map, find, uniqueId, isEmpty } from 'lodash';
import { debounce, map as mapFp } from 'lodash/fp';
import { useMemo, useContext, useRef, useEffect, useCallback } from 'react';

import StepperContentContainer from '../StepperContentContainer';

import ClientRepSection from './ClientRepSection';
import ProjectManagers from './ProjectManagers';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { KickOffContext } from '@/Contexts/KickOff';
import {
  AddressLocationTypeEnum,
  type GetClientsBasicQuery,
  GetKickOffPreloadDataDocument,
  GetProjectCodeSuggestionDocument,
  GetProjectDocument,
  KickOffStatusEnum,
  type Project,
} from '@/graphql/generated/graphql';
import NewAddressesForm from '@/shared-components/NewAddressesForm';
import { getSubProjectCodeInitials, objectLength } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';

type ClientOption = NonNullable<GetClientsBasicQuery['getClients']>[number] & {
  inputValue?: string;
};

type ProjectOption = Pick<Project, 'id' | 'title' | 'code'> & {
  inputValue?: string;
};

const mapCodes = mapFp('code');
const mapProjectId = mapFp('projectId');
const clientFilter = createFilterOptions<ClientOption>();
const projectFilter = createFilterOptions<ProjectOption>();
const normalDebounce = debounce(500);

const isProjectCodeUnique = (projectData: Project, code: string) => !mapCodes(projectData).includes(code);

const ProjectBasics: React.FC = () => {
  const {
    projectData,
    loadingContext,
    selectedClientId,
    clientName,
    clientCompanyNumber,
    kickOffTitle,
    addresses,
    setKickOffTitle,
    setSelectedClientId,
    setClientName,
    setClientCompanyNumber,
    setClientReps,
    setProjectData,
    setAddresses,
    setManagers,
    setActivities,
    setContractTypeIds,
    setProjectTagIds,
    setProjectSettings,
    selectedPreloadKickOff,
    onPreloadKickOffChange,
    newTemplate,
    setNewTemplate,
    newTemplateTitle,
    setNewTemplateTitle,
    selectedTemplate,
    onPreloadTemplateChange,
    managers,
    existingProjectId,
  } = useContext(KickOffContext);

  const { currentUser } = useContext(CurrentUserContext);
  const debounceFunc = useRef(normalDebounce((fn: () => void) => fn())).current;

  const { data: preloadData } = useQuery(GetKickOffPreloadDataDocument, {
    fetchPolicy: 'network-only',
    skip: !currentUser?.defaultCompany?.id,
    variables: { kickOffStatus: KickOffStatusEnum.Draft, companyId: currentUser?.defaultCompany?.id as string },
  });

  const { data: codeSuggestionQuery } = useQuery(GetProjectCodeSuggestionDocument, {
    fetchPolicy: 'network-only',
    skip: !projectData?.title || projectData.id !== '0',
    variables: { title: projectData?.title as string },
  });

  const [loadProjectData] = useLazyQuery(GetProjectDocument);

  // ── Derived ────────────────────────────────────────────────────────────────
  const clientValue = useMemo<ClientOption | null>(() => {
    if (selectedClientId) {
      return find(preloadData?.getClients, (c) => c.id === selectedClientId) ?? null;
    }
    if (clientName) return { id: '0', name: clientName };
    return null;
  }, [selectedClientId, clientName, preloadData?.getClients]);

  const isNewClient = !selectedClientId && !!clientName;
  const hasClient = !!selectedClientId || !!clientName;

  // ── Helpers ────────────────────────────────────────────────────────────────
  const resetClientState = () => {
    setSelectedClientId(null);
    setClientName(null);
    setClientCompanyNumber(null);
    setClientReps([]);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleProjectChange = useCallback(
    (value: ProjectOption) => {
      if (value.id === '0') {
        const newTitle = value.inputValue ?? value.title.replace(/Add "(.*?)"/, '$1');
        setProjectData({ id: '0', title: newTitle, code: '' });
        if (!selectedTemplate) {
          setAddresses([]);
          setManagers([]);
          setActivities([]);
          setContractTypeIds([]);
          setProjectTagIds([]);
          setProjectSettings([]);
          resetClientState();
        }
        return;
      }

      const draft = find(preloadData?.getCompanyKickOffs, (kickoff) => kickoff.projectId === value.id);

      onPreloadTemplateChange(null); // To make sure project load its own data for the first time

      if (draft) {
        onPreloadKickOffChange(draft);
      } else {
        onPreloadKickOffChange(null);
        setKickOffTitle(`Kick-Off - ${value.code ?? ''}-${value.title ?? ''} - Rev.0.0.1`);
        // TODO: FETCH THE PROJECT AND LOAD STATES
        loadProjectData({ variables: { projectId: value.id ?? '' } }).then((res) => {
          if (res.error) {
            displayNotification({ type: 'error', text: res.error.message });
            return;
          }

          if (res.data?.getProject) {
            const fullProject = res.data.getProject as Project;
            setProjectData(fullProject); // TODO: This is entirely wrong, fix this situation
            setSelectedClientId(fullProject.client?.id ?? null);
            setClientName(null);
            setClientCompanyNumber(null);
            setClientReps([]);

            setAddresses([
              ...(fullProject.mainAddress
                ? [
                    {
                      id: fullProject.id,
                      street: fullProject.mainAddress.street,
                      city: fullProject.mainAddress.city,
                      state: fullProject.mainAddress.state,
                      country: fullProject.mainAddress.country,
                      zip: fullProject.mainAddress.zip,
                      locationType: AddressLocationTypeEnum.Main,
                    },
                  ]
                : []),
              ...(fullProject.subLocations?.map((sub) => ({
                id: sub.id,
                street: sub.street,
                city: sub.city,
                state: sub.state,
                country: sub.country,
                zip: sub.zip,
                locationType: AddressLocationTypeEnum.Sub,
              })) ?? []),
            ]);

            setManagers(
              map(fullProject.managers ?? [], (m) => ({
                id: m.id,
                title: m.title,
                userType: m.userType,
                employmentId: m.employment?.id,
                userId: m.user?.id,
                companyResourceId: m.companyResource?.id ?? '',
              })),
            );
            setActivities(
              map(fullProject.projectActivities ?? [], (a) => ({
                id: uniqueId(),
                title: a.title,
                description: a.description ?? '',
                startTime: dayjs(a.startTime).format('YYYY-MM-DD'),
                endTime: dayjs(a.endTime).format('YYYY-MM-DD'),
                supervisorId: a.supervisor?.id ?? null,
                colorCode: a.colorCode,
              })),
            );
            setContractTypeIds(fullProject.contractTypeIds ?? []);
            setProjectTagIds(fullProject.tagIds ?? []);
            setProjectSettings(fullProject.projectSettings ?? []);
          }
        });
      }
    },
    [
      preloadData?.getCompanyKickOffs,
      onPreloadTemplateChange,
      onPreloadKickOffChange,
      setKickOffTitle,
      loadProjectData,
      setProjectData,
      setSelectedClientId,
      setClientName,
      setClientCompanyNumber,
      setClientReps,
      setAddresses,
      setManagers,
      setActivities,
      setContractTypeIds,
      setProjectTagIds,
      setProjectSettings,
      selectedTemplate,
      resetClientState,
    ],
  );

  useEffect(() => {
    if (existingProjectId && isEmpty(projectData)) {
      const project = find(
        map(preloadData?.getProjects?.edges, 'node') ?? [],
        (project) => project.id === existingProjectId,
      );
      if (project) {
        handleProjectChange({ id: project.id, title: project.title, code: project.code });
      }
    }
  }, [existingProjectId, projectData, preloadData?.getProjects, handleProjectChange]);

  const handleClientChange = (_: unknown, newValue: string | ClientOption | null) => {
    if (!newValue) {
      resetClientState();
      return;
    }

    if (typeof newValue === 'string') {
      setSelectedClientId(null);
      setClientName(newValue);
      setClientReps([]);
      return;
    }

    if (newValue.id === '0') {
      const name = newValue.inputValue ?? newValue.name.replace(/^Add "(.+)"$/, '$1');
      setSelectedClientId(null);
      setClientName(name);
      setClientReps([]);
    } else {
      setSelectedClientId(newValue.id);
      setClientName(null);
      setClientCompanyNumber(null);
      setClientReps([]);
    }
  };

  const handleKickOffTitleChange = (value: string) => {
    debounceFunc.cancel();
    debounceFunc(() => setKickOffTitle(value));
  };

  const handleCodeChange = (value: string) => {
    setProjectData({ ...projectData, code: value });

    if (projectData.id === '0' && projectData.title !== '') {
      setKickOffTitle(`Kick-Off - ${value}-${projectData.title ?? ''} - Rev.0.0.1`);
    }
  };

  if (loadingContext) {
    return (
      <StepperContentContainer>
        <LinearProgress />
      </StepperContentContainer>
    );
  }

  return (
    <StepperContentContainer dataTestId="kick-off-project-basics">
      <Grid container size={12}>
        <Grid size={6}>
          {objectLength(preloadData?.getCompanyKickOffs) > 0 ? (
            <Autocomplete
              options={preloadData!.getCompanyKickOffs!}
              getOptionLabel={(option) => option.title || 'Untitled Draft'}
              value={selectedPreloadKickOff}
              onChange={(_, newValue) => onPreloadKickOffChange(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.title}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a Draft to Load"
                  placeholder="Search existing drafts..."
                  fullWidth
                />
              )}
              fullWidth
              sx={{ mb: 3 }}
            />
          ) : (
            <TextField value="No drafts available" disabled fullWidth />
          )}
        </Grid>
        <Grid size={6}>
          {objectLength(preloadData?.getCompanyKickOffTemplates) > 0 ? (
            <Autocomplete
              options={preloadData!.getCompanyKickOffTemplates!}
              getOptionLabel={(option) => option.title ?? ''}
              value={selectedTemplate}
              onChange={(_, newValue) => onPreloadTemplateChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a Template to Load"
                  placeholder="Search existing drafts..."
                  fullWidth
                />
              )}
              fullWidth
              sx={{ mb: 3 }}
            />
          ) : (
            <TextField value="No Templates available" disabled fullWidth />
          )}
        </Grid>
      </Grid>

      {/* ── Kick-Off Title ─────────────────────────────────────────────────── */}
      <TextField
        label="Kick-Off Title"
        variant="outlined"
        defaultValue={kickOffTitle}
        key={`kickoff-title-${kickOffTitle}`}
        fullWidth
        onChange={(e) => handleKickOffTitleChange(e.target.value)}
      />

      {/* ── New Template ─────────────────────────────────────────────────── */}
      <Divider sx={{ width: '100%', mb: 1 }} textAlign="left">
        New Template
      </Divider>
      <Grid container size={12}>
        <FormControl component="fieldset">
          <FormGroup aria-label="position" row>
            <FormControlLabel
              checked={newTemplate}
              onChange={(_, checked) => {
                setNewTemplate(checked);
                if (!checked) setNewTemplateTitle('');
              }}
              control={<Switch color="primary" />}
              label="New Template"
              labelPlacement="end"
            />
          </FormGroup>
        </FormControl>
        {newTemplate && (
          <TextField
            label="Template Title"
            variant="outlined"
            value={newTemplateTitle}
            fullWidth
            onChange={(e) => setNewTemplateTitle(e.target.value)}
            error={!newTemplateTitle}
            helperText={!newTemplateTitle ? 'Template Title is required.' : ''}
          />
        )}
      </Grid>

      {/* ── Project ───────────────────────────────────────────────────────── */}
      <Divider sx={{ width: '100%', mb: 1 }} textAlign="left">
        Project
      </Divider>
      <Grid container size={12} spacing={1}>
        <Autocomplete
          data-testid="kick-off-project-selector"
          value={projectData as ProjectOption}
          options={(map(preloadData?.getProjects?.edges, 'node') ?? []) as ProjectOption[]}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (option.inputValue) return option.inputValue;

            return option.title ? `${option.title} (${option.code || 'No Code Assigned'})` : '';
          }}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          filterOptions={(options, params) => {
            const filtered = projectFilter(options, params);
            if (params.inputValue && !options.some((o) => params.inputValue === o.title)) {
              filtered.push({
                inputValue: params.inputValue,
                id: '0',
                title: `Add "${params.inputValue}"`,
                code: '',
              });
            }
            return filtered;
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => {
            if (newValue) handleProjectChange(newValue as ProjectOption);
          }}
          renderOption={(props, option) => {
            const hasDraft = mapProjectId(preloadData?.getCompanyKickOffs).includes(option.id);
            const { key, ...otherProps } = props;

            return (
              <Tooltip key={`${key}-${option.id}`} title={hasDraft ? 'Load draft' : ''} placement="top">
                <li {...otherProps}>
                  <Button
                    variant="text"
                    sx={{ pointerEvents: 'none', textAlign: 'left' }}
                    color="inherit"
                    endIcon={hasDraft ? <DriveFileRenameOutlineIcon color="warning" /> : <></>}
                  >
                    {`${option.title} (${option.code})`}
                  </Button>
                </li>
              </Tooltip>
            );
          }}
          fullWidth
          sx={{ width: '49%' }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Type a new title or select existing project"
              error={!projectData?.title}
              helperText={!projectData?.title ? 'Project Title is required' : ''}
            />
          )}
        />

        <TextField
          required
          disabled={!projectData.title || projectData.id !== '0'}
          error={
            Boolean(projectData.title) &&
            (!projectData?.code || !isProjectCodeUnique(preloadData?.getProjects as Project, projectData?.code))
          }
          helperText={
            projectData.id === '0' && !projectData.code ? (
              <Grid>
                <Typography variant="caption">Suggested:</Typography>
                <Button
                  size="small"
                  onClick={() => handleCodeChange(codeSuggestionQuery?.getProjectCodeSuggestion as string)}
                >
                  {getSubProjectCodeInitials(undefined)}
                  {codeSuggestionQuery?.getProjectCodeSuggestion}
                </Button>
              </Grid>
            ) : !isProjectCodeUnique(preloadData?.getProjects as Project, projectData.code as string) ? (
              'Project code must be unique'
            ) : (
              ''
            )
          }
          defaultValue={projectData?.code}
          key={projectData?.code}
          label="Project Code"
          onBlur={(e) => handleCodeChange(e.target.value)}
          slotProps={{
            htmlInput: { sx: { backgroundColor: 'white' } },
            formHelperText: { component: 'div' }, // Forces helperText to be a <div>
            input: {},
          }}
        />

        <ProjectManagers
          allManagers={preloadData?.getManagerAutocompleteOptions || preloadData?.getManagers || []}
          managers={managers}
          setManagers={setManagers}
        />
      </Grid>

      {/* ── Client ────────────────────────────────────────────────────────── */}
      <Divider sx={{ width: '100%' }} textAlign="left">
        Client Details
      </Divider>
      <Grid container size={12} spacing={1} alignItems="flex-start">
        <Autocomplete
          value={clientValue}
          options={(preloadData?.getClients ?? []) as ClientOption[]}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.name ?? '';
          }}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          freeSolo
          filterOptions={(options, params) => {
            const filtered = clientFilter(options, params);
            if (params.inputValue && !options.some((o) => params.inputValue === o.name)) {
              filtered.unshift({
                id: '0',
                name: `Add "${params.inputValue}"`,
                inputValue: params.inputValue,
              });
            }
            return filtered;
          }}
          onChange={handleClientChange}
          renderOption={(props, option) => {
            const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
            return (
              <li key={`${key}-${option.id}`} {...rest}>
                {option.name}
              </li>
            );
          }}
          sx={{ width: '49%' }}
          renderInput={(params) => (
            <TextField {...params} label="Client" placeholder="Type a new name or select existing client" />
          )}
        />

        {/* Company number — shown for new clients, required before finalization */}
        {isNewClient && (
          <TextField
            label="Company Number"
            variant="outlined"
            required
            value={clientCompanyNumber ?? ''}
            onChange={(e) => setClientCompanyNumber(e.target.value)}
            helperText="Required before finalising the kick-off"
            sx={{ width: '49%' }}
          />
        )}
      </Grid>

      {/* Client rep section — shown for both existing and new clients */}
      {hasClient && <ClientRepSection />}

      {/* ── Location ──────────────────────────────────────────────────────── */}
      <Divider sx={{ width: '100%' }} textAlign="left">
        Location Details
      </Divider>
      <NewAddressesForm addresses={addresses} onChange={(addresses) => setAddresses(addresses)} multiple />
    </StepperContentContainer>
  );
};

export default ProjectBasics;
